import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  dailyMessages as messages,
  getTodaysMessageIndex,
} from '../data/dailyMessages';

const archiveMessages = [
  { date: "December 20, 2024", text: "The path to enlightenment is not about reaching a destination, but about transforming the journey itself.", author: "Spiritual Wisdom" },
  { date: "December 19, 2024", text: "In every act of kindness, we plant seeds of love that will bloom in countless hearts.", author: "Teaching on Compassion" },
  { date: "December 18, 2024", text: "True strength lies not in conquering others, but in mastering oneself with patience and wisdom.", author: "Ancient Teaching" },
  { date: "December 17, 2024", text: "When we serve others selflessly, we discover the divine presence within ourselves.", author: "Karma Yoga" },
  { date: "December 16, 2024", text: "Meditation is not about emptying the mind, but about filling the heart with divine love.", author: "Meditation Guide" },
  { date: "December 15, 2024", text: "Every sunrise brings new hope, every sunset teaches us to let go with grace.", author: "Nature's Wisdom" }
];

const timelessWisdomQuotes = [
  { text: "The mind is everything. What you think, you become.", author: "Buddha" },
  { text: "Be the change that you wish to see in the world.", author: "Mahatma Gandhi" },
  { text: "The best way to find yourself is to lose yourself in the service of others.", author: "Mahatma Gandhi" },
  { text: "Where there is love, there is life.", author: "Mahatma Gandhi" }
];

const DailyMessage = () => {
  const [featuredIndex, setFeaturedIndex] = useState(() => getTodaysMessageIndex());
  const [currentMessageIndex, setCurrentMessageIndex] = useState(featuredIndex);
  const [currentDateStr, setCurrentDateStr] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    setCurrentDateStr(
      new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    );
  }, []);

  const handlePrev = () => {
    setCurrentMessageIndex(idx => (idx - 1 + messages.length) % messages.length);
  };

  const handleNext = () => {
    setCurrentMessageIndex(idx => (idx + 1) % messages.length);
  };

  const handleToday = () => {
    const next = getTodaysMessageIndex();
    setFeaturedIndex(next);
    setCurrentMessageIndex(next);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 4000);
    }
  };

  const currentMessage = messages[currentMessageIndex];

  return (
    <>
      <Helmet>
        <title>Daily Spiritual Messages - Shree Ram Ranuja Dham</title>
        <meta name="description" content="Receive daily spiritual wisdom and inspiration for your journey toward divine truth and inner peace." />
      </Helmet>

      {/* ── Page Header ─────────────────────────────────── */}
      <section className="pt-24 pb-12 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Daily Spiritual Messages</h1>
          <p className="text-xl text-white opacity-95">Wisdom and inspiration for your spiritual journey</p>
        </div>
      </section>

      {/* ── Today's Message ────────────────────────────── */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Today's Message</h2>
              <p className="text-lg text-gray-600">{currentDateStr}</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 text-center border border-orange-100">
              <div className="mb-8">
                <i className="fas fa-quote-left text-4xl text-orange-300 mb-6"></i>
              </div>
              <div className="daily-message-content">
                <blockquote className="text-xl md:text-2xl font-medium text-gray-850 mb-4 italic">
                  "{currentMessage.text}"
                </blockquote>
                <cite className="text-orange-600 font-semibold">— {currentMessage.author}</cite>
                <div className="text-sm text-gray-500 mt-4">
                  {currentMessageIndex === featuredIndex ? currentDateStr : `Quote ${currentMessageIndex + 1} of ${messages.length}`}
                </div>
              </div>
              <div className="mt-8">
                <i className="fas fa-quote-right text-4xl text-orange-300"></i>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-center mt-8 space-x-4">
              <button
                onClick={handlePrev}
                className="btn-secondary px-6 py-2.5 rounded-xl border-2 border-orange-200 hover:bg-orange-50 text-orange-600 font-bold transition-all flex items-center"
              >
                <i className="fas fa-chevron-left mr-2"></i>Previous
              </button>
              <button
                onClick={handleToday}
                className="btn-primary px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-650 text-white font-bold transition-all"
              >
                Today
              </button>
              <button
                onClick={handleNext}
                className="btn-secondary px-6 py-2.5 rounded-xl border-2 border-orange-200 hover:bg-orange-50 text-orange-600 font-bold transition-all flex items-center"
              >
                Next<i className="fas fa-chevron-right ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Message Archive ────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-805 mb-12">Recent Messages</h2>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {archiveMessages.map((msg, idx) => (
                <div key={idx} className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-lg shadow-md border border-orange-100/50 hover:shadow-lg transition-shadow">
                  <div className="text-sm text-orange-600 font-semibold mb-3">{msg.date}</div>
                  <blockquote className="text-gray-800 mb-4 italic leading-relaxed text-sm md:text-base">
                    "{msg.text}"
                  </blockquote>
                  <cite className="text-orange-600 font-medium text-sm">— {msg.author}</cite>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Timeless Wisdom Quotes ─────────────────────── */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-805 mb-12">Timeless Wisdom</h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {timelessWisdomQuotes.map((q, idx) => (
                <div key={idx} className="text-center">
                  <blockquote className="text-xl md:text-2xl text-gray-800 mb-4 italic leading-relaxed">
                    "{q.text}"
                  </blockquote>
                  <cite className="text-orange-600 font-semibold">— {q.author}</cite>
                  {idx < timelessWisdomQuotes.length - 1 && <hr className="border-orange-200 mt-8" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Subscribe Section ──────────────────────────── */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-850 mb-4">Receive Daily Messages</h2>
            <p className="text-lg text-gray-650 mb-8 font-medium">
              Subscribe to receive daily spiritual messages and inspiration directly in your inbox.
            </p>

            {subscribed && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-3 rounded-lg mb-6 shadow text-sm font-semibold max-w-md mx-auto">
                ✨ Subscribed successfully! Daily messages will reach you with guru's grace.
              </div>
            )}

            <form onSubmit={handleSubscribe} autoComplete="off" className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="search"
                inputMode="email"
                placeholder="Enter your email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className="px-4 py-3.5 border border-orange-200 rounded-xl focus:outline-none focus:border-orange-500 text-gray-700 bg-white flex-1 shadow"
              />
              <button
                type="submit"
                className="px-8 py-3.5 rounded-xl font-bold text-white transition-all duration-200 shadow"
                style={{ background: 'linear-gradient(to right, #f97316, #dc2626)' }}
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default DailyMessage;
